import React, { useEffect, useRef, useState, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import Logo from '../../assets/images/WashtaLogo.png';
import { RiFileList3Line } from 'react-icons/ri';
import { IoNotificationsOutline } from 'react-icons/io5';
import { AiOutlineLogout } from 'react-icons/ai';
import { IoPersonAddSharp, IoQrCodeOutline, IoHomeOutline } from 'react-icons/io5';
import { GiChart } from 'react-icons/gi';
import { CiCreditCard2 } from 'react-icons/ci';
import LogoutPopup from '../../components/logoutpopup/LogoutPopup';
import { BsFillPersonVcardFill, BsClipboardCheck } from 'react-icons/bs';
import { MdOutlineKeyboardArrowRight, MdOutlineKeyboardArrowDown, MdInsertChartOutlined, MdCategory, MdOutlineSettings } from 'react-icons/md';
import { LuUsers, LuBox } from 'react-icons/lu';
import { FaMapPin } from 'react-icons/fa';

import '../components/dashboardcom/app.css';

const BASE = '/super-admin-dashboard';
const SCROLL_KEY = 'adminSidebarScrollPos';
const DROPDOWN_KEY = 'adminSidebarOpenDropdown';
const linkClass = ({ isActive }) => (isActive ? 'adminactive-link' : 'adminnavlinkLinkatag');
const labelClass = ({ isActive }) => (isActive ? 'adminactive-link' : 'adminadminnavlinkLinkatag');
const NAV_SECTIONS = [
  {
    title: 'Analytics',
    items: [
      { to: 'sales', icon: GiChart, label: 'Sales' },
      { to: 'CRM', icon: LuUsers, label: 'CRM' },
    ],
  },
  {
    title: 'Operations',
    items: [
      { to: 'orders', icon: MdInsertChartOutlined, label: 'Orders' },
      { to: 'Location', icon: FaMapPin, label: 'Locations' },
      {
        id: 'applications',
        icon: BsFillPersonVcardFill,
        label: 'Applications',
        children: [
          { to: 'pending', label: 'Pending' },
          { to: 'approved', label: 'Approved' },
          { to: 'rejected', label: 'Rejected' },
        ],
      },
    ],
  },
  {
    title: 'Products',
    items: [
      { to: 'promo-code', icon: IoQrCodeOutline, label: 'Promo Code' },
      { to: 'service-catalog', icon: MdCategory, label: 'Service Catalog' },
    ],
  },
  {
    title: 'Monthly Packages',
    items: [
      { to: 'monthly-packages/wash-counts', icon: MdOutlineSettings, label: 'Wash Counts' },
      { to: 'monthly-packages/package-sets', icon: LuBox, label: 'Package Sets' },
      { to: 'monthly-packages/locations', icon: IoHomeOutline, label: 'Package Locations' },
      { to: 'monthly-packages/orders', icon: RiFileList3Line, label: 'Package Orders' },
      { to: 'monthly-packages/due-washes', icon: BsClipboardCheck, label: 'Due Washes' },
    ],
  },
  {
    title: 'Platform',
    items: [
      { to: 'send-notifications', icon: IoNotificationsOutline, label: 'Send Notification' },
      // { to: 'service-fee', icon: CiCreditCard2, label: 'Service Fee' },
    ],
  },
];

const BOTTOM_ITEMS = [{ to: 'add-agents', icon: IoPersonAddSharp, label: 'Add Agent' }];

// ---- Small presentational pieces ----------------------------------------
function SidebarLink({ to, icon: Icon, label }) {
  return (
    <li className="li-navbar">
      <NavLink to={`${BASE}/${to}`} end className={linkClass}>
        <Icon style={{ fontSize: 17, marginBottom: 4 }} />
        <p style={{ paddingBottom: 0 }} className={labelClass}>
          {label}
        </p>
      </NavLink>
    </li>
  );
}

function SidebarSubLink({ to, label }) {
  return (
    <li className="li-navbar">
      <NavLink style={{ fontWeight: 400, padding: 5 }} to={`${BASE}/${to}`} end className={linkClass}>
        <p style={{ fontWeight: 400, padding: 5, userSelect: 'none' }} className={labelClass}>
          {label}
        </p>
      </NavLink>
    </li>
  );
}

function SidebarDropdown({ id, icon: Icon, label, children, isOpen, onToggle }) {
  return (
    <li className="li-navbar">
      <div className={`dropdown-header ${isOpen ? 'open' : ''}`} onClick={() => onToggle(id)}>
        <Icon style={{ fontSize: 17, marginBottom: 4 }} />
        <p style={{ paddingBottom: 0, paddingTop: 0 }} className="adminnavlinkLinkatagDropdwon">
          {label}
        </p>
        {isOpen ? <MdOutlineKeyboardArrowDown className="dropdown-arrow" /> : <MdOutlineKeyboardArrowRight className="dropdown-arrow" />}
      </div>
      {isOpen && (
        <ul className="dropdown-list">
          {children.map((child) => (
            <SidebarSubLink key={child.to} {...child} />
          ))}
        </ul>
      )}
    </li>
  );
}

function AdminSidebar() {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(() => localStorage.getItem(DROPDOWN_KEY) || '');

  const scrollRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(DROPDOWN_KEY, openDropdown);
  }, [openDropdown]);
  useEffect(() => {
    const savedScroll = sessionStorage.getItem(SCROLL_KEY);
    if (savedScroll && scrollRef.current) {
      scrollRef.current.scrollTop = parseInt(savedScroll, 10);
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      sessionStorage.setItem(SCROLL_KEY, scrollRef.current.scrollTop);
    }
  }, []);

  const toggleDropdown = useCallback((id) => {
    setOpenDropdown((prev) => (prev === id ? '' : id));
  }, []);

  const toggleDrawer = () => setDrawerOpen((prev) => !prev);

  const handleLogout = () => {
    setModalOpen(false);
    localStorage.clear();
    navigate('/');
  };

  return (
    <>
      <div className="menu-icon" onClick={toggleDrawer}>
        &#9776;
      </div>
      <div style={{ backgroundColor: '#1a1a1a' }} className={`sidebar ${drawerOpen ? 'open' : ''}`}>
        <div className="logo">
          <img src={Logo} style={{ height: '100%', width: '100%', objectFit: 'contain' }} alt="Logo" />
        </div>
        <nav className="navbar-sidebar">
          <ul ref={scrollRef} onScroll={handleScroll} style={{ height: '70vh', overflow: 'scroll' }}>
            {NAV_SECTIONS.map((section) => (
              <React.Fragment key={section.title}>
                <p className="nav-section" style={{ paddingBottom: 10, paddingTop: 10 }}>
                  {section.title}
                </p>
                {section.items.map((item) =>
                  item.children ? (
                    <SidebarDropdown
                      key={item.id}
                      id={item.id}
                      icon={item.icon}
                      label={item.label}
                      children={item.children}
                      isOpen={openDropdown === item.id}
                      onToggle={toggleDropdown}
                    />
                  ) : (
                    <SidebarLink key={item.to} {...item} />
                  )
                )}
              </React.Fragment>
            ))}
          </ul>

          <ul className="ul-list-bottom" style={{ marginTop: '0em' }}>
            {BOTTOM_ITEMS.map((item) => (
              <SidebarLink key={item.to} {...item} />
            ))}
            <li className="li-navbar">
              <NavLink onClick={() => setModalOpen(true)} to="" end className="adminnavlinkLinkatag">
                <AiOutlineLogout style={{ fontSize: 17, marginBottom: 4 }} />
                <p style={{ paddingBottom: 0 }} className="adminadminnavlinkLinkatag">
                  Logout
                </p>
              </NavLink>
              <LogoutPopup isOpen={isModalOpen} onClose={() => setModalOpen(false)} onLogout={handleLogout} />
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
}

export default AdminSidebar;